/**
 * Standard site chrome — header + footer wrapping `<Outlet />` for the
 * content pages. Pulls visuals from @pilot-protocol/ui + the theme
 * tokens (--background, --foreground, --surface-elevated, etc).
 */
import { NavLink, Outlet } from "react-router-dom";
import type { CSSProperties } from "react";
import { Chip } from "@pilot-protocol/ui";

export function SiteLayout() {
  return (
    <div style={pageStyle}>
      <header style={headerStyle}>
        <NavLink to="/" style={brandStyle}>
          <Chip tone="accent" dot>PILOT PROTOCOL</Chip>
          <span style={kickerStyle}>alpha</span>
        </NavLink>
        <nav style={navStyle}>
          <HeaderLink to="/leaderboards">Leaderboards</HeaderLink>
          <HeaderLink to="/tournaments">Tournaments</HeaderLink>
          <a
            href="https://github.com/protoLabsAI/PilotProtocol"
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...navLinkStyle, color: "var(--foreground-secondary)" }}
          >
            GitHub
          </a>
        </nav>
      </header>

      <main style={mainStyle}>
        <Outlet />
      </main>

      <footer style={footerStyle}>
        <p style={{ margin: 0 }}>
          © {new Date().getFullYear()} Pilot Protocol. Made by{" "}
          <a
            href="https://protolabs.studio"
            style={footerLinkStyle}
            target="_blank"
            rel="noopener noreferrer"
          >
            protoLabs Studio
          </a>
          .
        </p>
        <div style={{ display: "flex", gap: 16 }}>
          <a
            href="https://github.com/protoLabsAI/PilotProtocol"
            style={footerLinkStyle}
            target="_blank"
            rel="noopener noreferrer"
          >
            GitHub
          </a>
        </div>
      </footer>
    </div>
  );
}

function HeaderLink({
  to,
  end,
  children,
}: {
  to: string;
  end?: boolean;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      style={({ isActive }) => ({
        ...navLinkStyle,
        color: isActive ? "var(--foreground)" : "var(--foreground-secondary)",
        borderBottomColor: isActive ? "var(--accent)" : "transparent",
      })}
    >
      {children}
    </NavLink>
  );
}

// ---------- Styles ----------

const pageStyle: CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  background: "var(--background)",
  color: "var(--foreground)",
  fontFamily: "var(--font-body)",
};

const headerStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  padding: "12px 24px",
  borderBottom: "1px solid var(--surface-elevated)",
};

const brandStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  textDecoration: "none",
  color: "inherit",
};

const kickerStyle: CSSProperties = {
  fontSize: "var(--text-xs)",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  color: "var(--foreground-muted)",
};

const navStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 16,
  fontSize: "var(--text-sm)",
};

const navLinkStyle: CSSProperties = {
  textDecoration: "none",
  paddingBottom: 4,
  borderBottom: "2px solid transparent",
  transition: "color 120ms ease, border-color 120ms ease",
};

const mainStyle: CSSProperties = {
  flex: 1,
  padding: "32px 24px",
  maxWidth: 1100,
  width: "100%",
  margin: "0 auto",
};

const footerStyle: CSSProperties = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  padding: "16px 24px",
  fontSize: "var(--text-sm)",
  color: "var(--foreground-secondary)",
  borderTop: "1px solid var(--surface-elevated)",
};

const footerLinkStyle: CSSProperties = {
  color: "var(--foreground-secondary)",
  textDecoration: "none",
};
